import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FiBell } from "react-icons/fi";
import { useSelector } from "react-redux";
import { RootState } from "../../State/store";
import CampaignIcon from "@mui/icons-material/Campaign";
import { useNtfy } from "../ui/ntfy";

interface Notification {
  id: number;
  campaignId: number;
  workspaceId: number;
  unread: boolean;
  statusMark: string;
  notificationType: string;
  createdAt: string;
  notificationData: Record<string, string>;
  role: string | null;
}

interface NotificationBellProps {
  workspaceId: number | null;
}

const NotificationBellOperator: React.FC<NotificationBellProps> = ({ workspaceId }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // mirrors advertiser controls
  const [isNtfyConnected, setIsNtfyConnected] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  const operatorUrl = useSelector((state: RootState) => state.authentication.operatorUrl);
  const apiUrlAdvAcc = useSelector((state: RootState) => state.authentication.apiURL); // kept if you need it elsewhere

  // ---- helpers ----
  const safeParse = <T,>(raw: unknown, fallback: T): T => {
    if (typeof raw !== "string") return (raw as T) ?? fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  };

  // 1) Initial load (once after mount / when workspaceId available)
  useEffect(() => {
    if (!workspaceId || !operatorUrl || hasInitialLoad) return;

    const loadInitial = async () => {
      try {
        const [countRes, listRes] = await Promise.all([
          axios.get(`${operatorUrl}/GetOperatorNotificationCount?workspaceId=${workspaceId}`),
          axios.get(`${operatorUrl}/GetOperatorNotifications?workspaceId=${workspaceId}`),
        ]);

        if (countRes.data?.status === "Success") {
          setUnreadCount(countRes.data.notificationCount ?? 0);
        }

        if (listRes.data?.status === "Success") {
          // backend shape uses snake_case: data[]
          const rows = (listRes.data.data ?? []) as any[];
          const mapped: Notification[] = rows
            .filter((n) => (n.role ?? "").toLowerCase() === "operator")
            .map((n) => ({
              id: n.id,
              campaignId: n.campaignid,
              workspaceId: n.workspaceid,
              unread: (n.statusmark ?? "Unread") === "Unread",
              statusMark: n.statusmark ?? "Unread",
              notificationType: n.notificationtype ?? "",
              createdAt: n.createdat ?? new Date().toISOString(),
              notificationData: safeParse<Record<string, string>>(n.notificationdata, {}),
              role: n.role ?? "Operator",
            }));

          setNotifications(mapped);
        }

        setHasInitialLoad(true);
      } catch (err) {
        console.error("Operator: initial load failed", err);
      }
    };

    loadInitial();
  }, [workspaceId, operatorUrl, hasInitialLoad]);

  // 2) Fallback polling ONLY when ntfy disconnected (same cadence as advertiser: 80s)
  useEffect(() => {
    if (!workspaceId || !operatorUrl || isNtfyConnected || !hasInitialLoad) return;

    const fetchCount = async () => {
      try {
        const r = await axios.get(
          `${operatorUrl}/GetOperatorNotificationCount?workspaceId=${workspaceId}`
        );
        if (r.data?.status === "Success") {
          setUnreadCount(r.data.notificationCount ?? 0);
        }
      } catch (e) {
        console.error("Operator: fallback polling failed", e);
      }
    };

    // run now, then interval
    fetchCount();
    // const h = setInterval(fetchCount,80000);
    // return () => clearInterval(h);
  }, [workspaceId, operatorUrl, isNtfyConnected, hasInitialLoad]);

  // 3) Manual fetch list (only when ntfy is NOT connected)
  const fetchLiveCampaigns = async () => {
    if (isNtfyConnected || !workspaceId || !operatorUrl) return;
    try {
      const res = await axios.get(
        `${operatorUrl}/GetOperatorNotifications?workspaceId=${workspaceId}`
      );
      const rows = (res.data?.data ?? []) as any[];
      debugger;

      const mapped: Notification[] = rows
        .filter((n) => (n.role ?? "").toLowerCase() === "operator")
        .map((n) => ({
          id: n.id,
          campaignId: n.campaignid,
          workspaceId: n.workspaceid,
          unread: (n.statusmark ?? "Unread") === "Unread",
          statusMark: n.statusmark ?? "Unread",
          notificationType: n.notificationtype ?? "",
          createdAt: n.createdat ?? new Date().toISOString(),
          notificationData: safeParse<Record<string, string>>(n.notificationdata, {}),
          role: n.role ?? "Operator",
        }));

      setNotifications(mapped);
      setUnreadCount(mapped.filter((x) => x.unread).length);
    } catch (e) {
      console.error("Operator: fetch list failed", e);
    }
  };

  // 4) ntfy subscription (mirrors advertiser parsing of evt.message.notificationdata)
  useNtfy(`${workspaceId ?? ""}_Operator`, (evt) => {
    debugger;
    // flip the connected flag as soon as we receive anything
      if (!isNtfyConnected) {
    setIsNtfyConnected(true);
  }
  debugger;

    const type = String(evt?.type || "").toLowerCase();

    if (type === "notification_created" || (!evt.type && evt.id)) {
      const unread = (evt.statusMark ?? evt.statusmark ?? "Unread").toLowerCase() === "unread";

      try {
        // Backends sometimes put the payload under "message" as a stringified JSON
        // { message: { notificationdata: "<stringified or object>" } }
        const msg = safeParse<any>(evt.message ?? {}, {});
        const notificationdata = safeParse<Record<string, string>>(msg.notificationdata ?? {}, {});

        setNotifications((prev) => [
          {
            id: evt.id,
            campaignId: evt.campaignId ?? evt.campaignid ?? 0,
            workspaceId: evt.workspaceId ?? evt.workspaceid ?? (workspaceId ?? 0),
            unread,
            statusMark: evt.statusMark ?? evt.statusmark ?? (unread ? "Unread" : "Read"),
            notificationType: evt.notificationType ?? evt.notificationtype ?? "",
            createdAt: evt.createdAt ?? evt.createdat ?? new Date().toISOString(),
            notificationData: notificationdata,
            role: evt.role ?? "Operator",
          },
          ...prev,
        ]);

        if (unread) setUnreadCount((c) => c + 1);
      } catch (e) {
        console.error("Operator: failed to parse ntfy message/notificationdata", e, evt);
      }
    }

    if (type === "notification_read" || type === "notification_bulk_read") {
      const ids: number[] = evt.ids ?? (evt.id ? [evt.id] : []);
      if (ids.length) {
        setNotifications((prev) =>
          prev.map((n) => (ids.includes(n.id) ? { ...n, unread: false, statusMark: "Read" } : n))
        );
        setUnreadCount((c) => Math.max(0, c - ids.length));
      }
    }
  });

  // 5) Mark all as read (single API like advertiser)
  const markAllAsRead = async () => {
    if (!workspaceId || !operatorUrl) return;
    try {
      const r = await axios.post(
        `${operatorUrl}/MarkOperatorNotificationRead?workspaceId=${workspaceId}`
      );
      if (r.data?.status === "Success") {
        setNotifications([]);
        setUnreadCount(0);
        setShowNotifications(false);
      } else {
        console.error("Operator: failed to mark all as read");
      }
    } catch (e) {
      console.error("Operator: mark all as read error", e);
    }
  };

  // 6) Bell click: only fetch via API if ntfy is NOT connected
  const handleBellClick = async () => {
    if (!isNtfyConnected) {
      await fetchLiveCampaigns();
    }
    const next = !showNotifications;
    setShowNotifications(next);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (next) {
      timeoutRef.current = setTimeout(() => setShowNotifications(false), 80000);
    }
  };

  // Close on outside click
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showNotifications]);

  // Cleanup timer
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div className="relative" ref={notificationRef}>
      <div onClick={handleBellClick} className="cursor-pointer">
        <FiBell style={{ width: "16px", height: "16px", color: "#0F172A" }} />
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold px-1 rounded-full min-w-[12px] h-[12px] flex items-center justify-center leading-none">
            {unreadCount}
          </span>
        )}
      </div>

      {showNotifications && (
        <div className="z-30 mr-6 absolute mt-4 right-0 transform translate-x-12 w-64 min-w-[280px] bg-white border border-gray-300 rounded shadow-lg p-3 overflow-visible">
          {notifications.length === 0 ? (
            <p className="text-gray-500 text-xs text-center">You have no unread notifications</p>
          ) : (
            <ul className={`${expanded ? "max-h-48" : "max-h-[96px]"} overflow-y-auto pr-1 divide-y divide-gray-200`}>
              {notifications
                .slice(0, expanded ? undefined : 2)
                .map((n) => {
                  const vals = Object.values(n.notificationData || {});
                  const msg = vals.length > 0 ? vals[0] : "You have a new notification.";
                  return (
                    <li
                      key={n.id}
                      className={`flex items-start text-xs py-2 text-left ${
                        n.unread ? "font-semibold text-slate-900" : "text-slate-700"
                      }`}
                    >
                      <CampaignIcon fontSize="small" className="text-blue-500 mr-2 mt-[2px]" />
                      <span className="leading-snug">{msg}</span>
                    </li>
                  );
                })}
            </ul>
          )}

          {notifications.length > 2 && (
            <div className="border-t border-gray-200 pt-2 text-center mt-2">
              <button
                className="text-xs text-blue-600 hover:underline"
                onClick={() => setExpanded((p) => !p)}
              >
                {expanded ? "Show Less" : "Show All"}
              </button>
            </div>
          )}

          {(notifications.length <= 2 || expanded) &&
            notifications.some((x) => x.unread) && (
              <>
                <hr className="my-2" />
                <button
                  className="w-full bg-blue-500 text-white py-1 rounded text-xs hover:bg-blue-600 transition"
                  onClick={markAllAsRead}
                >
                  Mark all as read
                </button>
              </>
            )}
        </div>
      )}
    </div>
  );
};

export default NotificationBellOperator;